// CONCEPT: the public date-picker API, with the picker library itself behind a dynamic import.
//
// @eonasdan/tempus-dominus is 83KB of JS and 46KB of CSS - the single largest dependency in
// main.js - and profiling every route in e2e/helpers/routes.ts found a date picker on six of
// them (history, lost-sales, stock-notifications, returns, reports, specials/add_edit). The
// other thirty-two were paying for it on first paint. Splitting it out is why this file exists;
// date-picker-impl.js is the previous contents, unchanged apart from its export names.
//
// USE: unchanged from before the split, deliberately.
//
//   initDateTimePicker($el, options)   attaches a picker; returns a PROMISE of the wrapper
//   getDateTimePicker($el)             returns the wrapper synchronously, or null
//
// The one contract change is that init's return value is now a promise. Nothing awaited it
// before the split - date-range-filter.js and control_panel/specials/add_edit.inc both call it
// for its side effect - and `getDateTimePicker` stays synchronous precisely so the existing
// `var p = getDateTimePicker($x); if (p) { ... }` call sites keep working untouched. Those
// guards already existed, because a picker was always absent until its element was in the DOM.
//
// What the promise does NOT cover is the calendar button: Tempus Dominus binds its toggle at
// construction, so between ready and the chunk landing that button does nothing at all.
// bridgeToggleWhilePending below closes that gap - see the comment on it.
//
// window.initDateTimePicker / window.getDateTimePicker are still installed by main.js, so
// inline view scripts see the same two globals they always have. That is the reason this
// wrapper is imported STATICALLY by main.js rather than lazily behind a DOM check: an inline
// `$(function () { initDateTimePicker(...) })` runs at DOM-ready and would throw a
// ReferenceError against a global that had not been imported yet. Same trap the DataTables
// chunk documents in data-tables.js - solved here by keeping the API eager and the library lazy.
import { registerPicker, lookupPicker } from './date-picker-registry.js';

let implPromise = null;

// Memoized: several pickers on one page must share one chunk load, not race four of them.
function loadImpl() {
    implPromise ??= import('./date-picker-impl.js');
    return implPromise;
}

export function initDateTimePicker($picker, options) {
    if (!$picker || !$picker.length) {
        return Promise.resolve(null);
    }

    // Returning the existing wrapper without touching the network preserves the old behaviour
    // of a second init on the same element being a no-op that hands back the first picker.
    const existing = lookupPicker($picker[0]);
    if (existing) {
        return Promise.resolve(existing);
    }

    // specials/add_edit is the only picker that uses defaultDate as the field's sole value:
    // the inputs have no HTML value, so before the split defaultDate landed in the same ready
    // turn. Now construction waits on the 83KB chunk, and a required empty input blocks Save
    // (and a later defaultDate would overwrite anything typed in the gap). Write a displayable
    // date onto an empty text input NOW so the form is valid without waiting for the widget.
    fillEmptyInputFromDefaultDate($picker, options);

    const pending = loadImpl()
        .then((mod) => mod.createDateTimePicker($picker, options))
        .catch((err) => {
            // A failed chunk load must not take the rest of the page's ready handler with it.
            // The field stays a plain text input, which is degraded but usable - every view
            // that has a picker also accepts a typed date.
            console.error('[date-picker] failed to load', err);
            return null;
        });

    bridgeToggleWhilePending($picker, pending);

    return pending;
}

function pickerTextInput($picker) {
    return $picker.find('input[type=text], input:not([type])')[0] || null;
}

function fillEmptyInputFromDefaultDate($picker, options) {
    if (!options || !options.defaultDate) {
        return;
    }

    const input = pickerTextInput($picker);
    if (!input || String(input.value).trim() !== '') {
        return;
    }

    const display = displayDate(options.defaultDate);
    if (display) {
        input.value = display;
    }
}

// MM/DD/YYYY, matching the specials picker `format`. Date-only ISO strings are calendar
// dates: `new Date('2026-03-04')` is UTC midnight and would show as the previous day in
// US timezones — the same trap `_toPlainDate` has, which this wrapper does not import.
function displayDate(value) {
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? '' : formatMdY(value);
    }

    if (typeof value === 'number') {
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? '' : formatMdY(parsed);
    }

    if (typeof value !== 'string') {
        return '';
    }

    const trimmed = value.trim();
    if (trimmed === '') {
        return '';
    }

    const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) {
        return iso[2] + '/' + iso[3] + '/' + iso[1];
    }

    const parsed = new Date(trimmed);
    return isNaN(parsed.getTime()) ? '' : formatMdY(parsed);
}

function formatMdY(d) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return mm + '/' + dd + '/' + d.getFullYear();
}

// Tempus Dominus binds its toggle listener when the widget is CONSTRUCTED, and construction now
// waits on a network round trip. Every picker view renders a visible calendar button whose only
// job is to open the widget - it carries no handler of its own, by design (see the comment above
// the button in history/index.inc, from review on #489). Before the split the widget existed in
// the same ready turn as the markup and there was no gap; now, between DOM-ready and the chunk
// resolving, that button is a dead control - and on the slow-4g this split was measured against,
// 83KB of JS plus 46KB of CSS is hundreds of milliseconds of gap.
//
// So remember a click that lands in the gap and open the calendar as soon as the widget exists.
// The listener comes off the moment the chunk resolves, which is what stops it from racing
// Tempus Dominus's own toggle on every click after that.
//
// CLICK, and only click. The first version of this also listened for pointerdown, on the
// reasoning that it fires first and covers touch - and that reintroduced the dead press it was
// written to fix, one handoff later. Tempus Dominus toggles on `click`, bound to the element
// itself when no data-td-target-toggle is set (tempus-dominus.js, _toggleClickEvent). A single
// press is pointerdown THEN click with a real gap between them, so the chunk can land in the
// middle: pointerdown sets the flag, the chunk resolves, this shows the calendar, and then the
// click arrives at Tempus Dominus's freshly bound handler and toggles it straight back shut.
//
// Gating on click alone makes the handoff clean, because click is exactly the event Tempus
// Dominus itself acts on. Either it lands before construction and is ours to replay, or it lands
// after and Tempus Dominus opens the calendar itself - never both. It is also the event Enter
// and Space on the button produce, which is the reason that trigger is a <button> and not a
// span, and it drops a press the user cancelled by dragging off the button before releasing.
const PENDING_NS = '.pace-date-picker-pending';

function bridgeToggleWhilePending($picker, pending) {
    let clickedWhilePending = false;

    $picker.on('click' + PENDING_NS, () => {
        clickedWhilePending = true;
    });

    pending.then((wrapper) => {
        $picker.off(PENDING_NS);

        if (clickedWhilePending && wrapper) {
            wrapper.show();
        }
    });
}

export function getDateTimePicker($picker) {
    if (!$picker || !$picker.length) {
        return null;
    }

    return lookupPicker($picker[0]);
}

export { registerPicker };
